import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  LinearProgress,
  Alert,
  Divider
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { toast } from 'react-toastify';
import api from '../../utils/api';

const emptyKr = () => ({
  title: '',
  weight_percent: 50,
  progress_percent: 0,
  objective_area_id: '',
  target_note: ''
});

export default function OkrObjectiveEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;
  const [loading, setLoading] = useState(!isNew);
  const [categories, setCategories] = useState([]);
  const [areas, setAreas] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [periodLabel, setPeriodLabel] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [status, setStatus] = useState('draft');
  const [krs, setKrs] = useState([emptyKr(), emptyKr()]);

  const loadMeta = useCallback(async () => {
    const [c, a] = await Promise.all([api.get('/okr/categories'), api.get('/okr/objective-areas')]);
    setCategories(c.data?.data || []);
    setAreas(a.data?.data || []);
  }, []);

  const loadOkr = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const r = await api.get(`/okr/${id}`);
      const o = r.data?.data;
      setTitle(o.title || '');
      setDescription(o.description || '');
      setCategoryId(o.category_id);
      setPeriodLabel(o.period_label || '');
      setPeriodStart(o.period_start || '');
      setPeriodEnd(o.period_end || '');
      setStatus(o.status || 'draft');
      const list = (o.keyResults || []).map((kr) => ({
        kr_code: kr.kr_code,
        title: kr.title,
        weight_percent: Number(kr.weight_percent),
        progress_percent: Number(kr.progress_percent),
        objective_area_id: kr.objective_area_id || '',
        target_note: kr.target_note || ''
      }));
      setKrs(list.length ? list : [emptyKr(), emptyKr()]);
    } catch (e) {
      toast.error('Failed to load objective');
      navigate('/okr/objectives');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, navigate]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    if (isNew && categories.length && !categoryId) {
      setCategoryId(categories[0].id);
    }
  }, [isNew, categories, categoryId]);

  useEffect(() => {
    loadOkr();
  }, [loadOkr]);

  const weightSum = krs.reduce((s, k) => s + Number(k.weight_percent || 0), 0);

  const save = async () => {
    if (Math.abs(weightSum - 100) > 0.02) {
      toast.error(`Key result weights must total 100% (currently ${weightSum.toFixed(2)}%).`);
      return;
    }
    const payload = {
      title,
      description,
      category_id: categoryId,
      period_label: periodLabel,
      period_start: periodStart || null,
      period_end: periodEnd || null,
      status,
      key_results: krs.map((k) => ({
        kr_code: k.kr_code,
        title: k.title,
        weight_percent: Number(k.weight_percent),
        progress_percent: Number(k.progress_percent),
        objective_area_id: k.objective_area_id || null,
        target_note: k.target_note
      }))
    };
    try {
      if (isNew) {
        const r = await api.post('/okr', payload);
        toast.success('Objective created');
        navigate(`/okr/objectives/${r.data?.data?.id}`);
      } else {
        await api.put(`/okr/${id}`, payload);
        toast.success('Saved');
        navigate(`/okr/objectives/${id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    }
  };

  const updateKr = (i, field, value) => {
    const next = [...krs];
    next[i] = { ...next[i], [field]: value };
    setKrs(next);
  };

  if (loading) return <LinearProgress />;

  return (
    <Box sx={{ p: 3, maxWidth: 960, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
        {isNew ? 'New objective' : 'Edit objective'}
      </Typography>

      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Title" value={title} onChange={(e) => setTitle(e.target.value)} required />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                minRows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Key Results category"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              >
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name} ({Number(c.weight_percent)}%)
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Period label" value={periodLabel} onChange={(e) => setPeriodLabel(e.target.value)} placeholder="Q1 2026" />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Start"
                InputLabelProps={{ shrink: true }}
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="End"
                InputLabelProps={{ shrink: true }}
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        Key results
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Each KR receives a system code (KR-XXXX-XXXX-XXXX). Weights must total exactly 100%.
      </Alert>

      {krs.map((kr, i) => (
        <Card key={i} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Key result {i + 1}
                {kr.kr_code ? ` · ${kr.kr_code}` : ''}
              </Typography>
              {krs.length > 1 && (
                <IconButton size="small" onClick={() => setKrs(krs.filter((_, j) => j !== i))}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={kr.title}
                  onChange={(e) => updateKr(i, 'title', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Objective area (module focus)"
                  value={kr.objective_area_id || ''}
                  onChange={(e) => updateKr(i, 'objective_area_id', e.target.value)}
                >
                  <MenuItem value="">—</MenuItem>
                  {areas.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Weight %"
                  value={kr.weight_percent}
                  onChange={(e) => updateKr(i, 'weight_percent', Number(e.target.value))}
                  inputProps={{ min: 0, max: 100, step: 0.5 }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Progress %"
                  value={kr.progress_percent}
                  onChange={(e) => updateKr(i, 'progress_percent', Number(e.target.value))}
                  inputProps={{ min: 0, max: 100, step: 0.5 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Target / notes"
                  value={kr.target_note}
                  onChange={(e) => updateKr(i, 'target_note', e.target.value)}
                  multiline
                  minRows={2}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}

      <Button startIcon={<AddIcon />} onClick={() => setKrs([...krs, emptyKr()])} sx={{ mb: 2 }}>
        Add key result
      </Button>

      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" sx={{ mb: 2, fontWeight: weightSum === 100 ? 600 : 700, color: weightSum === 100 ? 'success.main' : 'error.main' }}>
        Total weight: {weightSum.toFixed(2)}% {weightSum === 100 ? '✓' : '(must be 100%)'}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={save}>
          Save
        </Button>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
      </Box>
    </Box>
  );
}
